import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants/query-keys';
import type { UserStatus } from '@/types/schemas';

import {
  createRole,
  createStaff,
  deleteRole,
  listPermissions,
  listRoles,
  listStaff,
  listStaffOptions,
  resetStaffPassword,
  type StaffFilter,
  type StaffInput,
  updateRole,
  updateStaff,
} from '../api/staff.api';
import type { RoleInput } from '../schemas/staff.schema';

// --- Staff accounts ---------------------------------------------------------------------

export function useStaff(filter: StaffFilter) {
  return useQuery({
    queryKey: QUERY_KEYS.staff.list({ ...filter }),
    queryFn: ({ signal }) => listStaff(filter, signal),
    placeholderData: keepPreviousData,
  });
}

/**
 * The names an "owner" picker offers. The list barely changes, so it is cached for the session
 * rather than refetched every time a form opens.
 */
export function useStaffOptions(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.staff.options(),
    queryFn: ({ signal }) => listStaffOptions(signal),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateStaff() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.staff.all }),
      // A role's user count changes with the staff list.
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.all }),
    ]);
  };
}

export function useCreateStaff() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: (input: StaffInput) => createStaff(input),
    onSuccess: invalidate,
  });
}

export function useUpdateStaff() {
  const invalidate = useInvalidateStaff();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: number;
      input: Partial<Omit<StaffInput, 'password'>> & { status?: UserStatus };
    }) => updateStaff(id, input),
    onSuccess: invalidate,
  });
}

export function useResetStaffPassword() {
  return useMutation({ mutationFn: (id: number) => resetStaffPassword(id) });
}

// --- Roles ------------------------------------------------------------------------------

export function useRoles(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.roles.list(),
    queryFn: ({ signal }) => listRoles(signal),
    enabled,
  });
}

export function usePermissionCatalog(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.roles.permissions(),
    queryFn: ({ signal }) => listPermissions(signal),
    // The catalog only changes when the API is redeployed.
    staleTime: Infinity,
    enabled,
  });
}

function useInvalidateRoles() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.all });
}

export function useSaveRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: RoleInput }) =>
      id === null ? createRole(input) : updateRole(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({ mutationFn: (id: number) => deleteRole(id), onSuccess: invalidate });
}
